import { useState, useCallback } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";

import { useDispatch } from 'react-redux'
import { setStartRoomTour } from "../../new-user-experience/actions/onboarding-actions";
import { changeDsrPageDisplay, changeSelectSubpage } from "../../digital-sales-room/actions/dsr-actions";
import { waitForElement } from "../helpers/global-helpers";
import useLaunchdeckModal from "./use-launchdeck-modal";

const useRoomTour = ({
  faqPageId,
  overviewPageId,
  firstCustomPageId
}) => {
  const dispatch = useDispatch();

// Need to have different copies for whether it is in draft state or in active state
// Check displayed in host or guest perspective. 

  const [driverObj, setDriverObj] = useState(null);
  const { openLaunchdeckModal, closeLaunchdeckModal } = useLaunchdeckModal();

  const startTour = useCallback(() => {
    const tour = driver({
      showProgress: true,
      allowClose: false,
      showButtons: ["next"],
      overlayColor: "rgba(0, 0, 0, 0.5)",
      popoverOffset: 25,
      onDestroyed: () => {
        dispatch(setStartRoomTour(false));
      },
      onReset: () => {dispatch(setStartRoomTour(false))},
      onHighlighted: () => {dispatch(setStartRoomTour(true))},
      steps: [
        {
          element: '[data-driver="room-tour-step-1-A"]',
          popover: {
            title: "About this Room",
            description: `
            <div>
              <div style="margin-bottom: 1em;">A quick view on who's involved and what the Room is about</div>
            </div>`,
          },
        },
        {
          element: '[data-driver="room-tour-step-2"]',
          popover: {
            title: "Get up to speed quickly",
            description: "Latest Room activities & updates are all summarised here to bring you up to speed quickly.",
            onNextClick: () => {
              dispatch(changeSelectSubpage("updates-subpage"));
              tour.moveNext();
            },
          },
        },
        {
          element: '[data-driver="room-tour-step-3"]',
          popover: { title: "Engage with all members", description: "Interact & socialise with Room members over here - share content, pose a question, or simply to wish them a happy birthday!",
          onNextClick: () => {
            faqPageId && dispatch(changeDsrPageDisplay(faqPageId));
            tour.moveNext();
          }},
        },
        {
          element: '[data-driver="room-tour-step-4"]',
          popover: {
            title: "Empower members to resolve issues",
            description: "Empower your audience with DIY answers for commonly asked questions. People love it when they can get help quickly without delays.",
            onNextClick: () => {
              dispatch(changeSelectSubpage("action-list-subpage"));
              tour.moveNext();
            }
          },
        },
        {
          element: '[data-driver="room-tour-step-5"]',
          popover: {
            title: "Align everyone & advance together",
            description: "Present a clear plan of action to align understanding and move agendas together quickly",
            onNextClick: () => {
              dispatch(changeSelectSubpage("shared-docs-folder-subpage"));
              tour.moveNext();
            }
          },
        },
        {
          element: '[data-driver="room-tour-step-6"]',
          popover: {
            title: "Central knowledge hub",
            description: "Share important documents all in one place. Ensures members have access to them and are always using the right document versions.",
            onNextClick: () => {
              firstCustomPageId && dispatch(changeDsrPageDisplay(firstCustomPageId));
              tour.moveNext();
            }
          },
        },
        {
          element: '[data-driver="room-tour-step-7"]',
          popover: {
            title: "Customised content for your audiences",
            description: "Create custom pages containing curated content relevant to your stakeholders",
            onNextClick: async () => {
              overviewPageId && dispatch(changeDsrPageDisplay(overviewPageId));
              tour.moveNext();
            }
          },
        },
        {
          element: '[data-driver="room-tour-step-7A"]',
          popover: {
            title: "Manage Room members",
            description: "To get a sense of who's in the Room, use this button and...",
            onNextClick: async () => {
              openLaunchdeckModal('view-team-members')
              await waitForElement('[data-driver="room-tour-step-7B"]');
              tour.moveNext();
            }
          },
        },

        {
          element: '[data-driver="room-tour-step-7B"]',
          popover: {
            title: "Members in this Room",
            description: `
            <div>
              <div style="margin-bottom: 1em;">
                 ... a list of members will be shown to you. 
              </div>
              <div>
             You can decide to invite more members into the Room, or as a Room admin, restrict only specific people to join, through the use of joining access codes. 
               </div>
            </div>`,
              onNextClick: () => {
              closeLaunchdeckModal()
              tour.moveNext();
            },
          },
        },
        {
          element: '[data-driver="room-tour-step-8"]',
          popover: {
            title: "Done with the Room?",
            description: `To head elsewhere, use this button to get back to your homepage, manage your profile, or to log out.`,
          },
        },
      ],
    });

    setDriverObj(tour);
    tour.drive();

  }, [dispatch, faqPageId, overviewPageId, firstCustomPageId]);

  const stopTour = useCallback(() => {
    if (driverObj) {
      driverObj.stop();
    }
  }, [driverObj]);

  return { startTour, stopTour };
};

export default useRoomTour